import { IResponse, Response } from "@/common/entity/Response";
import axios, { AxiosRequestConfig } from "axios";
import { Message } from "element-ui";
import { RootStore, RootActionNames } from "@/vuex-store";

function addAccessToken(config: AxiosRequestConfig) {
  const accessToken = RootStore.state.currUser?.accessToken;
  if (accessToken) {
    config.data
      ? (config.data.accessToken = accessToken)
      : (config.data = {
          accessToken,
          ...config.data,
        });
  }
  return config;
}

function errorToResponse<Data>(error: any): Response<Data> {
  if (error instanceof Response) return error;

  if (error.response)
    //请求已经发出，但服务器响应的http状态码非2xx
    return error.response.status == 408
      ? Response.newForException(Response.MSG_REQUEST_TIME_OUT)
      : Response.newForException(Response.MSG_SERVER_EXCEPTION);

  if (error.request)
    //请求已经发出，但没有响应，一般是网络问题，请求超时，
    //找不到主机ip，网络地址错误等问题
    return Response.newForException(Response.MSG_NETWORK_EXCEPTION);

  //请求没有发出，在组装请求信息时发生异常
  return Response.newForException(Response.MSG_APP_EXCEPTION);
}

export const UrlPath = {
  /**
   * 获取微信登录二维码
   */
  loginQrCode: "/oa/login-qr-code",
  /**
   * 查询二维码状态
   */
  loginQrCodeStatus: "/oa/login-ticket",

  // -------------

  /**
   * 获取默认文章
   */
  defaultArticles: "/article/default",
  /**
   * 获取文章
   */
  article: "/article/get",
  /**
   * 更新或创建文章
   */
  updateArticle: "/article/update",
  /**
   * 短文本纠错
   */
  correct: "/article/correct",
  /**
   * 长文本纠错
   */
  longCorrect: "/article/long-correct",
  /**
   * 纠错记录
   */
  correction: "/article/correction",
  /**
   * 计算纠错次数
   */
  countCorrect: "/article/correct-count",

  // ---------------

  /**
   * 获取微信支付二维码
   */
  wxPayQrCode: "/wx/pay-qr-code",

  // -----------------

  /**
   * 支付套餐列表
   */
  payPkgs: "/other/pay-pkgs",
  /**
   * 获取用户配置信息
   */
  appConfigs: "/other/user-configs",

  // ---------------

  /**
   * 查询微信订单
   */
  queryWxOrder: "/wx/pay-order",

  // ---------------

  /**
   * 支付宝网页支付
   */
  aliPayForPage: "/ali-pay/pay-for-page",

  // --------------

  /**
   * 用户信息
   */
  userInfo: "/user/get-info",

  // --------------

  /**
   * 查询订单
   */
  queryOrder: "/pay-order/query",
};

export const DEFAULT_CONFIG: AxiosRequestConfig = {
  method: "POST",
  baseURL: process.env.VUE_APP_SERVER_URL,
  timeout: 5000,
  headers: {
    "Content-Type": "application/json; charset=UTF-8",
  },
};

/**
 * 发送请求
 * @param configOrUrl {@link axios.request}的请求配置参数，为字符串时相当
 * 于`{url: configOrUrl}`，默认参数为{@link DEFAULT_CONFIG}
 * @param isShowToast 请求异常或请求{@link Response.code 响应数据code}不为0时是否使用{@link Message.error}显示对应错误信息
 * @returns 成功时resolve返回{@link Response.data 响应数据}，失败时reject返回{@link Response 响应对象}
 */
export async function request<Data = any>(
  configOrUrl: AxiosRequestConfig | string,
  isShowToast = true
): Promise<Data> {
  const mayBeNullData = await requestNullData<Data>(configOrUrl, isShowToast);
  if (!mayBeNullData) {
    const response = Response.newForException(Response.MSG_SERVER_EXCEPTION);
    if (isShowToast) Message.error(response.userMsg);
    throw response;
  }
  return mayBeNullData;
}

/**
 * 发送请求
 * @param configOrUrl {@link axios.request}的请求配置参数，为字符串时相当
 * 于`{url: configOrUrl}`，默认参数为{@link DEFAULT_CONFIG}
 * @param isShowToast 请求异常或请求{@link Response.code 响应数据code}不为0时是否使用{@link Message.error}显示对应错误信息
 * @returns 成功时返回{@link Response.data 响应数据}，失败时返回{@link Response}对象
 */
export async function requestNullData<Data = any>(
  configOrUrl: AxiosRequestConfig | string,
  isShowToast = true
): Promise<Data | undefined> {
  if (typeof configOrUrl === "string") configOrUrl = { url: configOrUrl };
  configOrUrl = addAccessToken(configOrUrl);
  try {
    const { data: iResponse } = await axios.request<IResponse<Data>>({
      ...DEFAULT_CONFIG,
      ...configOrUrl,
    });
    if (!iResponse)
      throw Response.newForException(
        Response.MSG_SERVER_EXCEPTION,
        "响应body为空"
      );

    const response = new Response<Data>(iResponse);

    if (response.code === Response.CODE_LOGIN_TIME_OUT)
      await RootStore.dispatch(RootActionNames.logout);

    if (!response.isSuccess()) throw response;
    return response.data;
  } catch (error: any) {
    console.error(error);
    const response = errorToResponse(error);
    if (isShowToast) Message.error(response.userMsg);
    throw response;
  }
}
